import React, { useMemo } from 'react';

/**
 * Radio — Styled radio button with optional label.
 *
 * Uses a hidden native <input type="radio"> for accessibility and form
 * behavior, with custom visual rendering via .sc-circle and .sc-dot.
 *
 * Group radios by giving them the same `name` prop — standard HTML
 * radio group behavior handles mutual exclusivity.
 *
 * @param {boolean} checked   - Whether this radio is selected
 * @param {boolean} disabled  - Disabled state
 * @param {func}    onChange   - Change handler
 * @param {string}  label     - Optional label text
 * @param {string}  name      - Radio group name (shared across group members)
 * @param {string}  value     - Value submitted when this radio is selected
 */
export const Radio = ({
    checked = false,
    disabled = false,
    onChange,
    label,
    name,
    value,
    className = "",
    ...props
}) => {
    const computedClass = useMemo(() =>
        [
            "sc-radio",
            checked ? "sc-checked" : "",
            disabled ? "sc-disabled" : "",
            className
        ].filter(Boolean).join(" "),
        [checked, disabled, className]
    );

    return (
        <label className={computedClass}>
            {/* Hidden native radio — handles form behavior and accessibility */}
            <input 
                type="radio" 
                checked={checked} 
                disabled={disabled}
                onChange={onChange}
                name={name}
                value={value}
                {...props}
            />
            {/* Custom visual: outer circle + inner dot (visible when checked via CSS) */}
            <div className="sc-control">
                <div className="sc-circle">
                    <div className="sc-dot"></div>
                </div>
            </div>
            {label && <span className="sc-label body-m-semibold">{label}</span>}
        </label>
    );
};
